<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PostSaleCasesTableActions from './PostSaleCasesTableActions.vue';
import PostSaleCasesTableIdentityCells from './PostSaleCasesTableIdentityCells.vue';
import PostSaleCasesTableInProgressCells from './PostSaleCasesTableInProgressCells.vue';
import PostSaleCasesTableReviewCells from './PostSaleCasesTableReviewCells.vue';
import PostSaleCasesTableTerminalCells from './PostSaleCasesTableTerminalCells.vue';
import { columnsForPostSaleList } from './postSaleCasesTableColumns';

const props = defineProps({
  activeList: { type: String, required: true },
  cases: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['action', 'open']);
const { t } = useI18n();
const activeRowId = ref(null);

const columns = computed(() => columnsForPostSaleList(props.activeList));
const isReviewList = computed(() => props.activeList === 'revisoes');
const terminalList = computed(() =>
  ['finalizadas', 'cancelados'].includes(props.activeList)
    ? props.activeList
    : ''
);

watch(
  () => props.cases,
  cases => {
    activeRowId.value = cases[0]?.id || null;
  },
  { immediate: true }
);

const columnLabel = columnId => {
  if (columnId === 'post_sale') return t('POST_SALE.TABLE.POST_SALE');
  if (columnId === 'address') return t('POST_SALE.TABLE.ADDRESS');
  if (columnId === 'status') return t('POST_SALE.TABLE.STATUS');
  if (columnId === 'status_deadline') {
    return t('POST_SALE.TABLE.STATUS_DEADLINE');
  }
  if (columnId === 'entry_date') return t('POST_SALE.TABLE.ENTRY_DATE');
  if (columnId === 'payment') return t('POST_SALE.TABLE.PAYMENT');
  if (columnId === 'laudemium') return t('POST_SALE.TABLE.LAUDEMIUM');
  if (columnId === 'manager') return t('POST_SALE.TABLE.MANAGER');
  if (columnId === 'signature_date') return t('POST_SALE.TABLE.SIGNATURE_DATE');
  if (columnId === 'request_date') return t('POST_SALE.TABLE.REQUEST_DATE');
  if (columnId === 'elapsed_days') return t('POST_SALE.TABLE.ELAPSED_DAYS');
  if (columnId === 'corrections') return t('POST_SALE.TABLE.CORRECTIONS');
  if (columnId === 'completion_date') {
    return t('POST_SALE.TABLE.COMPLETION_DATE');
  }
  if (columnId === 'cancellation_date') {
    return t('POST_SALE.TABLE.CANCELLATION_DATE');
  }
  return '';
};
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full min-w-[1080px] text-left text-sm">
      <thead
        class="border-b-2 border-[#ffcc98] bg-white text-sm font-bold text-lais-text"
      >
        <tr>
          <th v-for="column in columns" :key="column.id" class="px-4 py-5">
            <span v-if="column.id !== 'actions'">
              {{ columnLabel(column.id) }}
            </span>
            <span v-else class="sr-only">
              {{ t('POST_SALE.ACTIONS.DETAILS') }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td
            :colspan="columns.length"
            class="h-28 p-4 text-left text-lais-text-muted"
          >
            {{ t('POST_SALE.COMMON.LOADING') }}
          </td>
        </tr>
        <template v-else>
          <tr
            v-for="item in cases"
            :key="item.id"
            class="h-[90px] border-b border-lais-border-soft transition hover:bg-lais-primary-50/30"
            @mouseenter="activeRowId = item.id"
            @focusin="activeRowId = item.id"
          >
            <PostSaleCasesTableIdentityCells
              :item="item"
              @open="emit('open', $event)"
            />
            <PostSaleCasesTableReviewCells v-if="isReviewList" :item="item" />
            <PostSaleCasesTableTerminalCells
              v-else-if="terminalList"
              :item="item"
              :list="terminalList"
            />
            <PostSaleCasesTableInProgressCells v-else :item="item" />
            <PostSaleCasesTableActions
              :active-list="activeList"
              :item="item"
              :visible="activeRowId === item.id"
              @action="emit('action', $event)"
            />
          </tr>
        </template>
        <tr v-if="!loading && !cases.length">
          <td
            :colspan="columns.length"
            class="h-36 p-4 align-top text-left text-lais-text-muted"
          >
            {{ t('POST_SALE.EMPTY.LIST') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
